/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import 'core-js/features/promise';
import 'regenerator-runtime/runtime';

import { Suspense } from 'react';
import { lazyWithRetry } from '@app/utils/loadable';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Security } from '@okta/okta-react';
import {
  BrowserRouter as Router,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';

import { Provider } from 'react-redux';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import {
  asyncWithLDProvider,
  withLDConsumer,
} from 'launchdarkly-react-client-sdk';

import MaintenancePage from '@app/containers/MaintenancePage';

import * as analytics from '@app/helpers/analytics';
import theme from '@app/theme';

import SnackbarProvider from '@app/containers/SnackbarProvider';
import ScrollToTop from '@app/components/ScrollToTop';
import I18nToggle from '@app/components/I18nToggle';
import PageImportFallback from '@app/components/PageImportFallback';

const App = lazyWithRetry(() => import('@app/containers/App'));

// Load the favicon - now handled by Vite from public directory
// import './images/favicon.ico'; // No longer needed - favicon is in public/
import { isUnsupportedBrowser } from '@app/helpers/browser';

import store from '@app/configureStore';
import UnsupportedBrowser from '@app/components/UnsupportedBrowser';
import { getOktaConfig } from '@app/okta.config';
import { getEnvConfig } from '@app/env.config';
import { shouldIgnoreError } from '@app/helpers/errorFiltering';

const { HOTJAR_ID, ENVIRONMENT } = getEnvConfig();

const MOUNT_NODE = document.getElementById('app');

const initializeHotjar = async () => {
  if (!HOTJAR_ID || !ENVIRONMENT || ENVIRONMENT === 'Local') {
    return;
  }

  const { hotjar } = await import('react-hotjar');
  // 6 is 'Hotjar Snippet Version' or hjsv
  hotjar.initialize({ id: HOTJAR_ID, sv: 6 });
};
void initializeHotjar();

const MaintenanceSwitch = withLDConsumer()(props => {
  return props.flags.maintenancePage ? (
    <MaintenancePage />
  ) : (
    <Suspense fallback={<PageImportFallback />}>
      <App />
    </Suspense>
  );
});

const SecuredRoutes = () => {
  const oktaConfig = getOktaConfig();
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const oktaAuth = new OktaAuth(oktaConfig.oidc);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <SnackbarProvider>
        <MaintenanceSwitch />
      </SnackbarProvider>
    </Security>
  );
};

const root = createRoot(MOUNT_NODE);

const render = async () => {
  const isUnsupported = isUnsupportedBrowser();
  const LDProvider = await asyncWithLDProvider({
    clientSideID: getEnvConfig().LAUNCHDARKLY_CLIENT_ID,
  });

  if (import.meta.env.VITE_ENABLE_MSW === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass',
    });
  }

  root.render(
    <Provider store={store}>
      <Router>
        <LDProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ScrollToTop />
            {ENVIRONMENT !== 'Production' && <I18nToggle />}
            {isUnsupported ? <UnsupportedBrowser /> : <SecuredRoutes />}
          </ThemeProvider>
        </LDProvider>
      </Router>
    </Provider>,
  );
};

const initializeSentry = async () => {
  const envConfig = getEnvConfig();
  if (!envConfig.SENTRY_DSN || !ENVIRONMENT || ENVIRONMENT === 'Local') {
    return;
  }

  const Sentry = await import('@sentry/react');

  const routingInstrumentation = Sentry.reactRouterV6BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  });

  Sentry.init({
    dsn: envConfig.SENTRY_DSN,
    environment: ENVIRONMENT,
    ...(envConfig.VERSION && {
      release: `bennie-web@${envConfig.VERSION}`,
    }),
    integrations: [routingInstrumentation],
    tracesSampleRate: 0.2,
    beforeSendTransaction(event) {
      if (
        !ENVIRONMENT ||
        ENVIRONMENT === 'Local' ||
        event.transaction === '/implicit/callback'
      ) {
        return null;
      }
      return event;
    },
    beforeSend(event, hint) {
      const originalError = hint.originalException;

      if (shouldIgnoreError(originalError)) {
        return null;
      }

      if (!ENVIRONMENT || ENVIRONMENT === 'Local') {
        return null;
      }

      const sanitizedEvent = { ...event };
      delete sanitizedEvent.user;
      return sanitizedEvent;
    },
  });
};

analytics.init(getEnvConfig().MIXPANEL_TOKEN, {
  debug: ENVIRONMENT !== 'Production',
});

(async () => {
  try {
    await initializeSentry();
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
  try {
    await render();
  } catch (error) {
    console.error('Failed to render app:', error);
  }
})();
